import { useEffect, useState, useCallback } from "react";
import { generateTimeSlots } from "@/utils";
import { DAYSOFWEEK } from "config/constant";
import {
    DayName,
    DayInputs,
    TimeInput,
    WorkingHourInput,
    ScheduleFormInputs,
    UseWorkingHoursProps,
    UseWorkingHoursReturn,
} from "context/schedule/types";

export const useWorkingHours = ({
    day,
    inputs,
    setInputs,
    disableTimeWindows,
    notify,
}: UseWorkingHoursProps): UseWorkingHoursReturn => {
    // ============================================================================
    // LOCAL STATE
    // ============================================================================

    const [dayInputs, setDayInputs] = useState<DayInputs>({
        working_hours: { start: "", end: "" },
        time_slots: [],
    });

    const [customizeTimeSlot, setCustomizeTimeSlot] = useState(false);
    const [applyChangesAllDays, setApplyChangesAllDays] =
        useState<DayName | null>(null);

    // ============================================================================
    // EFFECTS
    // ============================================================================

    // Sync with global inputs
    useEffect(() => {
        const currentDayData = inputs.working_hours?.find(
            wh => wh.day_of_the_week === day,
        ) || {
            start: "",
            end: "",
            day_of_the_week: day,
        };

        const currentTimeSlots =
            inputs.time_slots?.filter(ts => ts.day_of_the_week === day) || [];

        setDayInputs({
            working_hours: currentDayData,
            time_slots: currentTimeSlots,
        });
    }, [day, inputs]);

    // Handle disable time windows
    useEffect(() => {
        if (disableTimeWindows) {
            setDayInputs(prev => ({ ...prev, time_slots: [] }));
            setInputs(prev => {
                const remaining =
                    prev.time_slots?.filter(ts => ts.day_of_the_week !== day) ||
                    [];
                return { ...prev, time_slots: remaining };
            });
        }
    }, [disableTimeWindows, day, setInputs]);

    // ============================================================================
    // HELPER FUNCTIONS
    // ============================================================================

    const resetToggles = useCallback(() => {
        setApplyChangesAllDays(null);
        setCustomizeTimeSlot(false);
    }, []);

    const saveWorkingHours = useCallback(
        (workingHour: WorkingHourInput & { day_of_the_week: DayName }) => {
            setInputs(prev => {
                const updated =
                    prev.working_hours?.filter(
                        w => w.day_of_the_week !== day,
                    ) || [];
                return {
                    ...prev,
                    working_hours: [...updated, workingHour],
                };
            });
        },
        [day, setInputs],
    );

    const saveTimeSlots = useCallback(
        (slots: TimeInput[]) => {
            setInputs(prev => {
                const withoutDay =
                    prev.time_slots?.filter(ts => ts.day_of_the_week !== day) ||
                    [];
                return {
                    ...prev,
                    time_slots: [...withoutDay, ...slots],
                };
            });
        },
        [day, setInputs],
    );

    // ============================================================================
    // WORKING HOURS HANDLERS
    // ============================================================================

    const handleWorkingHourChange = useCallback(
        (field: keyof WorkingHourInput, value: string) => {
            setDayInputs(prev => {
                const updatedInputs = {
                    ...prev,
                    working_hours: {
                        ...prev.working_hours,
                        [field]: value,
                        day_of_the_week: day,
                    },
                };
                saveWorkingHours(updatedInputs.working_hours!);
                setApplyChangesAllDays(null);
                return updatedInputs;
            });
        },
        [day, saveWorkingHours],
    );

    // ============================================================================
    // TIME SLOT HANDLERS
    // ============================================================================

    const handleTimeSlotChange = useCallback(
        (index: number, field: keyof TimeInput, value: any) => {
            setDayInputs(prev => {
                const updatedSlots = [...prev.time_slots];
                updatedSlots[index] = {
                    ...updatedSlots[index],
                    [field]: value,
                    day_of_the_week: day,
                };
                saveTimeSlots(updatedSlots);
                return { ...prev, time_slots: updatedSlots };
            });
            resetToggles();
        },
        [day, saveTimeSlots, resetToggles],
    );

    const handleAddTimeSlot = useCallback(() => {
        const newSlot: TimeInput = {
            start: "",
            end: "",
            cutoff_time: "",
            disable: false,
            day_of_the_week: day,
            is_express: false,
        };

        setDayInputs(prev => {
            const updated = [...prev.time_slots, newSlot];
            saveTimeSlots(updated);
            return { ...prev, time_slots: updated };
        });
        resetToggles();
    }, [day, saveTimeSlots, resetToggles]);

    const handleRemoveTimeSlot = useCallback(
        (index: number) => {
            setDayInputs(prev => {
                const updated = prev.time_slots.filter((_, i) => i !== index);
                saveTimeSlots(updated);
                return { ...prev, time_slots: updated };
            });
            resetToggles();
        },
        [saveTimeSlots, resetToggles],
    );

    // ============================================================================
    // AUTO-GENERATION HANDLERS
    // ============================================================================

    const toggleCustomizeTimeSlot = useCallback(
        (start?: string, end?: string) => {
            const nextStart = start ?? dayInputs.working_hours?.start;
            const nextEnd = end ?? dayInputs.working_hours?.end;

            if (!nextStart || !nextEnd) return;

            const slots = generateTimeSlots(day, nextStart, nextEnd);
            setDayInputs(prev => ({ ...prev, time_slots: slots }));
            saveTimeSlots(slots);
        },
        [day, dayInputs.working_hours, saveTimeSlots],
    );

    // ============================================================================
    // APPLY TO ALL DAYS HANDLERS
    // ============================================================================

    const applyChangeAllDays = useCallback(() => {
        const wh = dayInputs.working_hours;
        const ts = dayInputs.time_slots;

        if (!wh?.start || !wh?.end) {
            notify("Working hours must be set to apply to all days", "error");
            return;
        }

        const working_hours = DAYSOFWEEK.map((d: DayName) => ({
            start: wh.start,
            end: wh.end,
            day_of_the_week: d,
        }));

        const time_slots = DAYSOFWEEK.flatMap((d: DayName) =>
            ts.map(slot => ({ ...slot, day_of_the_week: d })),
        );

        setInputs(prev => ({ ...prev, working_hours, time_slots }));
        notify(`Changes applied to all days of the week`, "success");
        setApplyChangesAllDays(day);
    }, [dayInputs, day, setInputs, notify]);

    // ============================================================================
    // RETURN VALUES
    // ============================================================================

    return {
        dayInputs,
        customizeTimeSlot,
        applyChangesAllDays,
        handleWorkingHourChange,
        handleTimeSlotChange,
        handleAddTimeSlot,
        handleRemoveTimeSlot,
        toggleCustomizeTimeSlot,
        applyChangeAllDays,
        setCustomizeTimeSlot,
    };
};
